/* eslint-disable @typescript-eslint/naming-convention */
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { HotToastService } from '@ngneat/hot-toast';
import { DataCollectorService } from 'app/core/services/data_collector_services/data_collector_service.service';
import { LocalisationService } from 'app/core/services/localisation/zuper.localisation.service';
import { CreateForm, CreateFormField } from '../data-picker.interface';
import { ContactInfoService } from '../../../components/contact-info/contact-info.component.service';
import { DataPickerService } from '../../../core/services/data_picker/data_picker.service';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import { TimezonePickerService } from 'app/core/services/timezone/timezone-picker.service';
import { Observable } from 'rxjs';

@Component({
    selector: 'quick-create-organization',
    templateUrl: './quick-create-organization.component.html',
    styleUrls: ['./quick-create-organization.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class QuickCreateOrganizationComponent implements OnInit, OnChanges {
    @Input() formReset: boolean = false;
    @Input() formSubmit: boolean = false;
    @Output() eventEmitter: EventEmitter<any> = new EventEmitter();
    @Output() formValidationEmitter: EventEmitter<boolean> = new EventEmitter();

    organizationForm: UntypedFormGroup = null;
    loading: boolean = false;
    timezones: Observable<any[]> = null;

    createOrganization: CreateForm[] = [{
        headerName: 'Primary Information',
        headerDisplayKey: 'text.primary_information',
        canShow: true,
        formFields: [{
            labelName: 'Organization Name',
            labelDisplayKey: 'text.organization_name',
            fieldId: 'name',
            fieldType: 'input',
            inputType: 'text',
            formControlName: 'name',
            placeHolder: 'Organization Name',
            placeHolderDisplayKey: 'text.organization_name',
            isMandatory: true,
            validationText: 'Organization Name field is required',
            validationTextKey: 'create.organization.name.validText'
        }, {
            labelName: 'Email',
            labelDisplayKey: 'email',
            fieldId: 'email',
            fieldType: 'input',
            inputType: 'email',
            formControlName: 'email',
            placeHolder: 'Email',
            placeHolderDisplayKey: 'text.email_placeholder',
            isMandatory: true,
            validationText: 'Email is required',
            validationTextKey: 'create.customer.email.validText',
            emailValidationText: 'Invalid email address',
            emailValidationTextKey: 'text.invalid_email_address'
        },
        {
            labelName: 'Preferred Timezone',
            labelDisplayKey: 'preferred.timezone',
            fieldId: 'organization_timezone',
            fieldType: 'dropdown',
            formControlName: 'organization_timezone',
            placeHolder: 'Preferred Timezone',
            placeHolderDisplayKey: 'preferred.timezone',
            observable: this.timezones,
            oberservableKey: 'display_name',
            oberservableValue: 'key',
            showIf: this.utilityService.isTimeZoneEnabled
        }
    ]
    }, {
        headerName: 'Service Address',
        headerDisplayKey: 'text.service_address',
        headerType: 'address',
        formGroupName: 'serviceAddress',
        canShow: true,
        formFields: [],
        contactFields: {
            contactFirstName: false,
            contactLastName: false,
            contactEmail: false,
            contactPhone: false,
            street: true,
            enableMapsAutoComplete: true,
            canTruncate: true,
            landmark: false,
            city: true,
            state: true,
            country: true,
            zipcode: true,
            latitude: false,
            longitude: false
        }
    }, {
        headerName: 'Billing Address',
        headerDisplayKey: 'billing.address.text',
        headerType: 'address',
        formGroupName: 'billingAddress',
        canShow: true,
        isMatCheck: true,
        formControlName: 'sameAddress',
        matCheckLabel: 'text.same_as_service_address',
        formFields: [],
        contactFields: {
            contactFirstName: false,
            contactLastName: false,
            contactEmail: false,
            contactPhone: false,
            street: true,
            enableMapsAutoComplete: false,
            landmark: false,
            city: true,
            state: true,
            country: true,
            zipcode: true,
            latitude: false,
            longitude: false
        }
    }];
    formControls: any[] = [{
        formControlName: 'name',
        value: '',
        validators: [Validators.required]
    }, {
        formControlName: 'email',
        value: '',
        validators: [Validators.email, Validators.required]
    },{
        formControlName: 'organization_timezone',
        value: null,
    },{
        formControlName: 'serviceAddress',
        controls: []
    }, {
        formControlName: 'billingAddress',
        controls: []
    }, {
        formControlName: 'sameAddress',
        value: true,
        validators: []
    }];
    pickerAPIUrls: any;

    constructor(
        private dataCollectorService: DataCollectorService,
        private localisationService: LocalisationService,
        private pickerService: DataPickerService,
        private contactInfoService: ContactInfoService,
        private toast: HotToastService,
        private utilityService: UtilityService,
        public timezoneService: TimezonePickerService,
        private eleRef: ElementRef) {
        this.organizationForm = this.pickerService.createFormControls(this.formControls);
    }

    ngOnInit(): void {
        this.localisationService.setDefaultLanguage();
        this.pickerAPIUrls = this.pickerService.pickerAPIUrls;
        this.timezones = this.timezoneService.timeZones;
        this.openQuickCreateForm();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.formReset?.currentValue === true) {
            this.resetForm();
        }

        if (changes?.formSubmit?.firstChange === false && changes?.formSubmit?.currentValue === true) {
            this.submitQuickCreateForm();
        }
    }

    openQuickCreateForm(): void {
        this.organizationForm.get('sameAddress').valueChanges.subscribe((value: any) => {
            if (!value) {
                this.updateBillingAddress();
            }
        });
    }

    resetForm(): void {
        this.organizationForm.reset({
            sameAddress: true
        });
    }

    updateBillingAddress(): void {
        const serviceAddress: any = this.organizationForm.get('serviceAddress').value;
        this.organizationForm.get('billingAddress').patchValue(serviceAddress);
    }

    getErrorMessageKey(field: CreateFormField): string {
        if (this.organizationForm.get(field.formControlName).hasError('required')) {
            return field.validationTextKey;
        }

        if (field.inputType === 'email' && this.organizationForm.get(field.formControlName).hasError('email')) {
            return field.emailValidationTextKey;
        }

        return '';
    }

    showError(formControlName: string): boolean {
        return this.organizationForm.get(formControlName)?.touched && !this.organizationForm.get(formControlName)?.valid;
    }

    checkFieldsContainerVisibility(header: CreateForm): boolean {
        if (header.isMatCheck) {
            return this.organizationForm.get(header.formControlName) ? !this.organizationForm.get(header.formControlName).value : true;
        }
        return true;
    }

    submitQuickCreateForm(): void {
        this.organizationForm.markAllAsTouched();
        if (this.organizationForm.get('sameAddress').value) {
            this.updateBillingAddress();
        }
        if (this.organizationForm.valid) {
            this.createNewOrganization();
        } else {
            this.pickerService.scrollToFirstInvalidControl(this.eleRef.nativeElement);
            this.formValidationEmitter.emit(false);
        }
    }

    createNewOrganization(): void {
        const payLoad: any = this.getOrganizationModel(this.organizationForm.getRawValue());
        this.dataCollectorService.postData(this.pickerAPIUrls.organizationUrl, payLoad)
            .subscribe(
                (response: any) => {
                    this.toast.success(response.message);
                    this.dataCollectorService.getData(this.pickerAPIUrls.organizationUrl + '/' + response.data.organization_uid).subscribe(
                        (organization: any) => {
                            this.eventEmitter.emit({ value: [organization.data] });
                        }, (_error: any) => {
                        });
                }, (_error: any) => {
                    this.formValidationEmitter.emit(false);
                }
            );
    }

    getOrganizationModel(formValue: any): any {
        const organizationAddress: any = this.contactInfoService.getContactInfoAddressModel(formValue['serviceAddress']);
        const billingAddress: any = this.contactInfoService.getContactInfoAddressModel(formValue['billingAddress']);
        return {
            organization: {
                custom_fields: [],
                customers: [],
                organization_address: organizationAddress,
                organization_billing_address: billingAddress,
                organization_email: formValue.email,
                organization_name: formValue.name,
                ...(this.utilityService.isTimeZoneEnabled && {organization_timezone: formValue.organization_timezone})
            }
        };
    }

    getObservable(type: string): Observable<any[]> {
        if (type === 'organization_timezone') {
            return this.timezones;
        }
    }
}
